import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { TextField } from "ui/text-field";
import CollegeProfileIterableApiKeyInput from "containers/admin/clients/client/college-profile-page/college-profile-iterable-api-key-input";

import type { ClientAttributes } from "hooks/api/use-clients";
import { useCurrentUser } from "hooks/api/use-current-user";

import { inputIsAlphanumeric, inputIsLowercase } from "utilities";
import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";

export type BasicInformationFormValues = {
  iterableProjectId: string;
  clientCode: string;
  tealiumProfileCode: string;
  apolloDomainUrl: string;
  collegeUrl: string;
  privacyPolicyUrl: string;
};

export type CollegeProfileBasicInformationFormProps = {
  formId: string;
  client: ClientAttributes;
  onSubmit: (values: BasicInformationFormValues) => void;
  onDirtyChange?: (isDirty: boolean) => void;
};

const buildDefaultValues = (client: ClientAttributes): BasicInformationFormValues => ({
  iterableProjectId: client.iterableProjectId ?? "",
  clientCode: client.clientCode ?? "",
  tealiumProfileCode: client.tealiumProfileCode ?? "",
  apolloDomainUrl: client.apolloDomainUrl ?? "",
  collegeUrl: client.collegeUrl ?? "",
  privacyPolicyUrl: client.privacyPolicyUrl ?? "",
});

const TEALIUM_PROFILE_CODE_SPACES_ERROR =
  "The Client Apollo profile code cannot contain spaces - please remove them.";
const TEALIUM_PROFILE_CODE_FORMAT_ERROR =
  "The Client Apollo profile code must only include lower-case alphanumeric characters - please correct.";

function validateTealiumProfileCode(value: string): true | string {
  if (!value) return true;
  if (value.includes(" ")) return TEALIUM_PROFILE_CODE_SPACES_ERROR;
  if (!inputIsAlphanumeric(value) || !inputIsLowercase(value)) return TEALIUM_PROFILE_CODE_FORMAT_ERROR;

  return true;
}

/**
 * Editable form for College profile's Basic information section. Mirrors the
 * field set/copy of the legacy `BasicInfo` component (see
 * `containers/admin/clients/client/components/basic-info/basic-info.tsx`),
 * rewritten with `react-hook-form` + `ui/*` inputs.
 */
const CollegeProfileBasicInformationForm = ({
  formId,
  client,
  onSubmit,
  onDirtyChange,
}: CollegeProfileBasicInformationFormProps) => {
  const currentUser = useCurrentUser();
  const hasAdvancedPermission = hasPermission(currentUser, Permission.PERM_CLIENT_ADVANCED);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<BasicInformationFormValues>({ defaultValues: buildDefaultValues(client) });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <form
      id={formId}
      className="flex max-w-[600px] flex-col gap-4"
      onSubmit={handleSubmit((values) =>
        onSubmit({ ...values, clientCode: values.clientCode.toUpperCase() }),
      )}
    >
      <TextField
        required
        label="Client Iterable project ID"
        helperText="Please enter unique code for this client. This will be used to map campaign creative to Iterable workflows."
        errMessage={errors.iterableProjectId?.message}
        {...register("iterableProjectId", { required: "Client Iterable project ID is required." })}
      />
      <Controller
        name="clientCode"
        control={control}
        rules={{ required: "Client prefix is required." }}
        render={({ field }) => (
          <TextField
            {...field}
            required
            label="Client prefix"
            helperText="Please select unique 2–4 digit code for this client. This will be prefix for content IDs for this client."
            errMessage={errors.clientCode?.message}
            slotProps={{ input: { maxLength: 4 } }}
            onChange={(event) => field.onChange(event.target.value.toUpperCase())}
          />
        )}
      />
      <CollegeProfileIterableApiKeyInput client={client} />
      <TextField
        disabled={!hasAdvancedPermission}
        label="Apollo profile code"
        helperText="Please enter unique code for this client using all lowercase characters with no spaces. This will be used to capture user events using Tealium scripts and identify the CDN storage directory for landing pages."
        errMessage={errors.tealiumProfileCode?.message}
        {...register("tealiumProfileCode", { validate: validateTealiumProfileCode })}
      />
      <TextField
        required
        label="Client apollo URL"
        helperText="Please include full address for program digital assets."
        errMessage={errors.apolloDomainUrl?.message}
        {...register("apolloDomainUrl", { required: "Client apollo URL is required." })}
      />
      <TextField
        label="Client website URL"
        helperText="Please include full address starting with https://."
        errMessage={errors.collegeUrl?.message}
        {...register("collegeUrl")}
      />
      <TextField
        label="Client privacy policy URL"
        helperText="Please include full address starting with https://."
        errMessage={errors.privacyPolicyUrl?.message}
        {...register("privacyPolicyUrl")}
      />
    </form>
  );
};

export default CollegeProfileBasicInformationForm;
